import {
  EXERCISE_KEY,
  EXERCISE_TYPE,
  EXERCISE_TYPE_MESSAGE,
  getByIdExerciseSelector,
  getCurrentWorkoutSectionSelect,
  getCurrentWorkoutSelect,
  updateNotesWorkoutExerciseAction
} from '@gymaholic/packages'
import { LoadingIcon } from 'components/Icon/icons/loading'
import NotFound from 'components/NotFound'
import PageTitle from 'components/Page/Page.Title'
import { View } from 'components/View'
import formatMessageIntlUtil from 'modules/intl/utils/format-message'
import getErrorReduxUtil from 'modules/redux/utils/get-error'
import getValueReduxUtil from 'modules/redux/utils/get-value'
import { isExerciseLoadingByIdSelector } from 'modules/training/modules/training-exercises/selectors'
import {
  isEmptyUtil,
  logUtil
} from 'packages/src'
import React from 'react'
import { defineMessages } from 'react-intl'
import useIntl from 'modules/component/utils/hooks/useIntl'
import useDispatch from 'modules/component/utils/hooks/useDispatch.js'
import useSelector from 'modules/component/utils/hooks/useSelector.js'
import ExerciseWrapper from 'pages/Exercise/components/Wrapper'
import ExerciseSetData from 'pages/Exercise/components/SetData/index.js'
import ExerciseContent from 'pages/Exercise/components/Content/index.js'
import getMusclesWorkedStrByIdExerciseSelector from 'modules/exercise/redux/selectors/get-muscles-worked-str-by-id/index.js'
import getExerciseNamePluralExerciseUtil from 'pages/Exercise/utils/get-exercise-name-plural/index.js'
import goBackRouterAction from 'modules/router/actions/go-back/index.js'
import { isMobileScreenViewportSelect } from 'modules/viewport/selectors/index.js'
import isBodyWeightExerciseUtil from '@gymaholic/packages/src/modules/exercise/model/utils/is-body-weight/index.js'
import { ExerciseHeader } from 'pages/Exercise/components/Header'
import { ExerciseVideo } from 'pages/Exercise/components/Video'
import { ExerciseMuscles } from 'pages/Exercise/components/Muscles'
import { ExerciseEquipment } from 'pages/Exercise/components/Equipment'
import { ExerciseType } from 'pages/Exercise/components/Type'
import { ExerciseNotes } from 'pages/Exercise/components/Notes'
import { ExerciseCheckFormButton } from 'pages/Exercise/components/CheckFormButton'
import { getExerciseMedia } from 'pages/Exercise/selectors/getExerciseMedia'
import { getExerciseData } from 'pages/Exercise/selectors/getExerciseData'
import { getLocationState } from 'pages/Exercise/selectors/getLocationState'
import { getExerciseUrl } from 'pages/Exercise/utils/getExerciseUrl'
import { getForceString } from 'pages/Exercise/utils/getForceString'
import { handleNavigateToOrigin as navigateToOrigin } from 'pages/Exercise/utils/handleNavigateToOrigin'
import { useExerciseMount } from 'pages/Exercise/hooks/useExerciseMount'
import { useExerciseWorkout } from 'pages/Exercise/hooks/useExerciseWorkout'

const messages = defineMessages({
  exercise: {
    id: 'Exercise.exercise',
    defaultMessage: 'Exercise'
  },
  whatMuscles: {
    id: 'Exercise.whatMuscles',
    defaultMessage: 'What muscles do {exerciseNamePlural} work?'
  },
  force: {
    id: 'Exercise.force',
    defaultMessage: 'It’s a {exerciseType} exercise that uses {force} force.'
  },
  exerciseType: {
    id: 'Exercise.exerciseType',
    defaultMessage: 'Exercise type'
  },
  muscleWorked: {
    id: 'Exercise.muscleWorked',
    defaultMessage: 'Muscle worked'
  },
  equipmentNeeded: {
    id: 'Exercise.equipmentNeeded',
    defaultMessage: 'Equipment needed'
  }
})

export function ExercisePage (props) {
  const isMobileScreen = useSelector(isMobileScreenViewportSelect)
  const intl = useIntl()
  const dispatch = useDispatch()
  const exerciseId = Number(props.match.params.id)
  const { isReadOnly } = props

  // Selectors
  const exercise = useSelector(getByIdExerciseSelector(exerciseId))
  const {
    posterUrl,
    videoUrl
  } = useSelector(getExerciseMedia(exerciseId))
  const {
    equipment,
    type,
    muscles,
    level,
    exerciseName,
    force,
    secondaryMuscles,
    setData,
    currentWorkoutExercise
  } = useSelector(getExerciseData(exercise))
  const {
    location,
    replacingWorkoutExerciseId,
    isReplacing,
    isVideoUrl,
    shouldShowContent
  } = useSelector(getLocationState)
  const isExerciseLoading = useSelector(isExerciseLoadingByIdSelector(exerciseId))
  const currentWorkoutSection = useSelector(getCurrentWorkoutSectionSelect)
  const currentWorkout = useSelector(getCurrentWorkoutSelect)
  const musclesWorkedStr = useSelector(
    getMusclesWorkedStrByIdExerciseSelector({
      id: exerciseId,
      intl
    })
  )

  // Local state
  const [note, setNote] = React.useState('')
  const [isNoteVisible, setIsNoteVisible] = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  // Computed values
  const error = getErrorReduxUtil(exercise)
  const exerciseType = formatMessageIntlUtil(intl, EXERCISE_TYPE_MESSAGE[type]).toLowerCase()
  const forceStr = getForceString({
    force,
    exerciseType,
    intl
  })
  const exerciseNamePlural = getExerciseNamePluralExerciseUtil(exerciseName)
  const url = getExerciseUrl({
    exerciseName,
    exerciseId,
    location,
    isVideoUrl
  })

  const canCheckForm = type !== EXERCISE_TYPE.ACTIVITY

  // Handlers
  const handleNavigateToOrigin = (workoutId) => {
    navigateToOrigin({
      location,
      workoutId,
      dispatch
    })
  }

  const handleClickNoteDone = () => {
    setIsNoteVisible(false)
    dispatch(updateNotesWorkoutExerciseAction(note))
  }

  const handleGoBack = () => {
    dispatch(goBackRouterAction())
  }

  // Effects
  useExerciseMount({
    exercise,
    exerciseId,
    isReadOnly,
    currentWorkout,
    location,
    dispatch
  })

  useExerciseWorkout({
    exercise,
    exerciseId,
    isReadOnly,
    isReplacing,
    currentWorkout,
    currentWorkoutSection,
    location,
    loading,
    setLoading,
    handleNavigateToOrigin,
    dispatch
  })

  // Debug logging
  logUtil('Exercise').debug('debugProps=', {
    exerciseId,
    exercise,
    isReadOnly,
    location,
    error,
    isVideoUrl,
    shouldShowContent
  })

  if (
    (
      !location.state &&
      !isReadOnly
    ) || (
      !isEmptyUtil(error)
    )
  ) {
    return (
      <NotFound />
    )
  }

  if (
    isExerciseLoading === undefined ||
    isExerciseLoading
  ) {
    return (
      <View
        bgc='white'
        h='100vh'
        centered
      >
        <LoadingIcon
          stroke='black'
          style={{
            width: '30px',
            height: '30px'
          }}
        />
      </View>
    )
  }

  const wrapperProps = {
    exerciseName,
    url,
    id: exerciseId,
    isBodyweight: isBodyWeightExerciseUtil(exercise)
  }

  const whatMuscles = (
    formatMessageIntlUtil(
      intl,
      messages.whatMuscles,
      { exerciseNamePlural }
    )
  )

  /**
   * {ExerciseContentProps}
   */
  const propsToPass = {
    exercise,
    exerciseId,
    exerciseName,
    exerciseNamePlural,
    exerciseType,
    musclesWorkedStr,
    isMobileScreen,
    whatMuscles,
    forceStr,
    secondaryMuscles,
    equipment,
    type,
    muscles,
    level
  }

  if (isReadOnly) {
    return (
      <ExerciseWrapper {...wrapperProps}>
        <ExerciseHeader
          isMobileScreen={isMobileScreen}
          onGoBack={handleGoBack}
          isReadOnly={isReadOnly}
        />

        <View
          m={isMobileScreen ? 'unset' : '0 auto'}
          w={isMobileScreen ? 'unset' : '500px'}
        >
          <ExerciseVideo
            videoUrl={videoUrl}
            posterUrl={posterUrl}
            isMobileScreen={isMobileScreen}
            shouldAutoPlay
          />
          <View
            px={isMobileScreen ? 1.5 : 0}
            mt
          >
            <PageTitle>{exerciseName}</PageTitle>
            {canCheckForm && <ExerciseCheckFormButton exerciseId={exerciseId} />}
          </View>

          {
            shouldShowContent && (
              <>
                <ExerciseContent
                  {...propsToPass}
                />
              </>
            )
          }
        </View>
      </ExerciseWrapper>
    )
  }

  return (
    <ExerciseWrapper {...wrapperProps}>
      <ExerciseHeader
        isMobileScreen={isMobileScreen}
        onGoBack={handleGoBack}
        isReadOnly={isReadOnly}
        isReplacing={isReplacing}
        loading={loading}
        setLoading={setLoading}
        exerciseId={exerciseId}
        workoutExerciseId={replacingWorkoutExerciseId}
        navigateBackToOrigin={handleNavigateToOrigin}
      />
      <ExerciseVideo
        videoUrl={videoUrl}
        posterUrl={posterUrl}
        isMobileScreen={isMobileScreen}
      />
      <View
        m={isMobileScreen
          ? 'unset'
          : '0 auto'}
        w={isMobileScreen
          ? 'unset'
          : '500px'}
        px={1.5}
      >
        <PageTitle>{getValueReduxUtil(exercise, EXERCISE_KEY.NAME)}</PageTitle>
        <ExerciseMuscles
          muscles={muscles}
          exercise={exercise}
        />
        <ExerciseEquipment
          equipment={equipment}
        />
        <ExerciseType
          type={type}
        />
        <ExerciseNotes
          note={note}
          isNoteVisible={isNoteVisible}
          onNoteChange={(event) => setNote(event.target.value)}
          onToggleNoteVisibility={() => setIsNoteVisible(true)}
          onNoteDone={handleClickNoteDone}
        />
        {
          !!setData && (
            <ExerciseSetData
              setData={setData}
              currentWorkoutExercise={currentWorkoutExercise}
            />
          )
        }
      </View>

    </ExerciseWrapper>
  )
}
